import { useState } from "react";
import getApiUrl from "../../../../config/api";
import {
  CopyIcon,
  RetryIcon,
  ThumbsUpFilledIcon,
  ThumbsUpOutlineIcon,
  ThumbsDownActiveIcon,
  ThumbsDownOutlineIcon,
  FeedbackModal,
} from "../../AiChat/components";

// Root Agent messages are not in the legacy PIMS conversation database. Use
// the general feedback form, including the exchange so reviewers have context.
export default function ProductMessageActions({
  message,
  language,
  currentUser,
  sessionId,
  isBusy,
  onRetry,
}) {
  const [liked, setLiked] = useState(false);
  const [reported, setReported] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const zh = language === "zh";
  const buttonClass =
    "self-start px-1.5 md:px-2 py-1 md:py-1.5 rounded-md border border-white/20 transition-colors flex items-center gap-1 text-xs bg-black/30 dark:bg-white/10 hover:bg-black/40 dark:hover:bg-white/20 text-white disabled:opacity-50 disabled:cursor-not-allowed";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message.text);
      setNotice(zh ? "已複製" : "Copied");
    } catch {
      setNotice(
        zh
          ? "無法複製，請選取文字手動複製。"
          : "Could not copy. Please select and copy the text manually.",
      );
    }
  };

  const submitFeedback = async () => {
    if (!reason.trim() || isSubmitting) return;
    setIsSubmitting(true);
    setError("");
    try {
      const token = await currentUser.getIdToken();
      const form = new FormData();
      form.append(
        "name",
        currentUser.displayName || currentUser.email || currentUser.uid,
      );
      form.append("email", currentUser.email || "");
      form.append("category", "other");
      form.append("subject", "TGlobal Product Assistant answer feedback");
      form.append(
        "message",
        [
          `Reason: ${reason.trim()}`,
          `Session: ${sessionId}`,
          `Message: ${message.id}`,
          `Time: ${new Date(message.startedAt).toISOString()}`,
          `Question: ${message.question}`,
          `Answer: ${message.text}`,
        ].join("\n\n"),
      );
      const response = await fetch(`${getApiUrl()}/send-feedback`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error("Feedback failed");
      setReported(true);
      setLiked(false);
      setIsOpen(false);
      setNotice(
        zh
          ? "已提交，感謝您的反饋。"
          : "Submitted. Thank you for your feedback.",
      );
    } catch {
      setError(
        zh
          ? "無法提交反饋，請確認您有反饋權限後重試。"
          : "Could not submit feedback. Check that you have feedback access and try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-1 md:gap-2 items-center">
        {message.text && (
          <>
            <button
              type="button"
              onClick={copy}
              className={buttonClass}
              title={zh ? "複製到剪貼簿" : "Copy to clipboard"}
            >
              <CopyIcon />
              <span>{zh ? "複製" : "Copy"}</span>
            </button>
            <button
              type="button"
              aria-label={zh ? "讚" : "Thumbs up"}
              aria-pressed={liked}
              onClick={() => {
                setLiked(!liked);
                setReported(false);
              }}
              className={`${buttonClass} ${liked ? "!bg-green-500/20 !text-green-700 dark:!text-green-400" : ""}`}
            >
              {liked ? <ThumbsUpFilledIcon /> : <ThumbsUpOutlineIcon />}
            </button>
            <button
              type="button"
              aria-label={zh ? "不喜歡" : "Thumbs down"}
              aria-pressed={reported}
              disabled={reported}
              onClick={() => {
                setReason("");
                setError("");
                setIsOpen(true);
              }}
              className={`${buttonClass} ${reported ? "!bg-red-500/20 !text-red-700 dark:!text-red-400" : ""}`}
            >
              {reported ? <ThumbsDownActiveIcon /> : <ThumbsDownOutlineIcon />}
            </button>
          </>
        )}
        <button
          type="button"
          disabled={isBusy}
          onClick={() => onRetry(message.question)}
          className={buttonClass}
          title={
            zh
              ? "以新一輪對話重新提問"
              : "Ask this question again as a new turn"
          }
        >
          <RetryIcon />
          <span>{zh ? "重試" : "Retry"}</span>
        </button>
      </div>
      {notice && (
        <p
          role="status"
          className="mt-1 text-xs text-gray-600 dark:text-gray-300"
        >
          {notice}
        </p>
      )}
      {isOpen && (
        <FeedbackModal
          language={language}
          isFeedbackSubmitting={isSubmitting}
          setFeedbackReason={setReason}
          onClose={() => setIsOpen(false)}
          onSubmit={submitFeedback}
          error={error}
        />
      )}
    </div>
  );
}
